/**
 * Shared browse controls (web).
 *
 * These are the three controls the storefront header is built from. They are
 * here rather than inline so Order Food and Shop Local cannot drift apart,
 * and so the "pills are for chips, filters and segmented controls ONLY" rule
 * from the app redesign is enforced by there being a real segmented control
 * to reach for.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, Grid2X2, ShoppingBag } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING, RADII, TAP_TARGET, TYPE } from "./tokens";

/* ── Segmented control ────────────────────────────────────────────────
   Delivery | Collection. A true segmented control: one track, a moving
   selected segment, exactly one active option.                         */

export function Segmented<T extends string>({
  options, value, onChange, ariaLabel, className,
}: {
  options: ReadonlyArray<{ value: T; label: string; icon?: ReactNode }>;
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(RADII.sm, "inline-flex items-center bg-slate-100 p-1 gap-1", className)}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              TYPE.caption, RADII.xs, FOCUS_RING,
              // 44px tall target on touch; visually compact on desktop.
              "inline-flex items-center gap-1.5 px-3 h-9 sm:h-8 transition-colors cursor-pointer whitespace-nowrap",
              active
                ? "bg-white text-[#1e3a5f] shadow-[0_1px_3px_rgba(15,23,42,0.06)]"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ── Filter chip ──────────────────────────────────────────────────────
   The interactive counterpart to the display-only <Chip>. Used for the
   quick filters (Open now / Offers / Clear).                           */

export function FilterChip({
  children, icon, selected, onClick, tone = "neutral",
}: {
  children: ReactNode;
  icon?: ReactNode;
  selected?: boolean;
  onClick: () => void;
  tone?: "neutral" | "success" | "warning";
}) {
  const selectedTone = {
    neutral: "bg-slate-800 text-white border-slate-800",
    success: "bg-emerald-600 text-white border-emerald-600",
    warning: "bg-amber-500 text-white border-amber-500",
  }[tone];

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        TYPE.caption, RADII.pill, FOCUS_RING,
        "inline-flex shrink-0 items-center gap-1.5 px-3 h-9 sm:h-8 border transition-colors cursor-pointer whitespace-nowrap",
        selected
          ? selectedTone
          : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/* ── Category rail ────────────────────────────────────────────────────
   Horizontal, scrollable, sticky-capable. Replaces the left sidebar the
   browse page used to spend a whole column on — that column is worth more
   as another card of restaurants.                                       */

export function CategoryRail({
  items, selected, onSelect, emoji, images, className,
}: {
  items: string[];
  selected: string;
  onSelect: (item: string) => void;
  /** Optional emoji per category name. */
  emoji?: Record<string, string>;
  images?: Record<string, string>;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // Arrows are only useful when there is somewhere to scroll to.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => setOverflowing(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  // Keep the selected category in view — it can be selected from elsewhere
  // (e.g. restoring state) and would otherwise sit off-screen.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selected]);

  const scroll = (dir: -1 | 1) =>
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });

  return (
    <div className={cn("relative group/rail", className)}>
      <div
        ref={scrollRef}
        role="tablist"
        aria-label="Filter by category"
        className="relative flex gap-2 overflow-x-auto scrollbar-none scroll-smooth py-1"
      >
        {items.map((item) => {
          const active = selected === item;
          return (
            <button
              key={item}
              ref={active ? activeRef : undefined}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSelect(item)}
              className={cn(
                TYPE.caption, RADII.pill, FOCUS_RING, TAP_TARGET,
                images
                  ? "flex shrink-0 flex-col items-center gap-2 w-20 py-2 transition-colors cursor-pointer"
                  : "inline-flex shrink-0 items-center gap-1.5 px-3.5 h-9 border transition-colors cursor-pointer whitespace-nowrap",
                images
                  ? active ? "text-[#1e3a5f] font-bold" : "text-slate-600 hover:text-slate-900"
                  : active ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
              )}
            >
              {images ? <span className={cn("w-[72px] h-[72px] rounded-full overflow-hidden grid place-items-center bg-slate-100 border-2", active ? "border-[#1e3a5f]" : "border-transparent")}>
                {images?.[item] ? <img src={images[item]} alt="" loading="lazy" className="w-full h-full object-cover" /> : item === "All" ? <Grid2X2 size={22} aria-hidden /> : <ShoppingBag size={22} aria-hidden />}
              </span> : emoji?.[item] ? <span aria-hidden>{emoji[item]}</span> : null}
              {item}
            </button>
          );
        })}
      </div>

      {overflowing && [-1, 1].map((dir) => (
        <button
          key={dir}
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={() => scroll(dir as -1 | 1)}
          className={cn(
            "hidden lg:grid place-items-center absolute top-1/2 -translate-y-1/2 z-10",
            "w-8 h-8 rounded-full bg-white shadow-md border border-slate-200 text-slate-700",
            "opacity-0 group-hover/rail:opacity-100 transition-opacity hover:bg-slate-50",
            dir === -1 ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2",
          )}
        >
          {dir === -1 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      ))}
    </div>
  );
}
